<script lang="ts" setup>
  import { FlowCard } from '@/shared/components/rebranding/FlowCard'
  import { getDurationDate } from '@/shared/composables/useEntry'
  import { getAbTest } from '@/shared/composables/useGetAbTest'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { usePerformanceTiming } from '@/shared/composables/usePerformanceTiming'
  import { usePrepareInboundAddon } from '@/shared/composables/usePrepareInboundAddon'
  import { INBOUND_ADDON_CODE, INBOUND_ADDON_KEYS } from '@/shared/constants/other.constants'
  import { inboundCheckResponse } from '@/shared/constants/validationMessages.constants'
  import { useCoreStore, useVisitorsStore } from '@/shared/store'
  import { setFormatDate } from '@/shared/utils'
  import AlertQC from '@qicgroupdoha/qic-connect-ds/components/AlertQC'
  import LoaderQC from '@qicgroupdoha/qic-connect-ds/components/LoaderQC'
  import { computed, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  interface Emits {
    (e: 'proceed', page?: string): void
    (e: 'load', callback: () => Promise<void>, requestId?: string, withLoader?: boolean): void
    (e: 'error', value: string): void
  }

  const emit = defineEmits<Emits>()

  const { locale, t } = useI18n()
  const visitorsStore = useVisitorsStore()
  const policyDuration = visitorsStore.holder.policyDuration || visitorsStore.entryValue
  const coreStore = useCoreStore()
  const gaService = useGoogleAnalytics()
  const { onPerformanceCheckEnd } = usePerformanceTiming()

  const durations = computed(() => coreStore.durations)

  const policyDurationDays = computed(() =>
    policyDuration
      ? (durations.value.find((entry) => policyDuration === entry.code)?.duration ?? 0)
      : 0
  )

  const getPolicyDates = () => {
    const startDate = visitorsStore.visitDate
    const endDate = getDurationDate(policyDuration, startDate, durations.value)
    return `${formatDate(startDate)} - ${formatDate(endDate)}`
  }

  const formatDate = (date: Date) => setFormatDate(date, locale.value as LocaleType)

  const isLoading = ref(false)
  const visaNumber = visitorsStore.holder.visa
  const visaValidityDate = visitorsStore.holder.visaValidityDate
  const visitDates = computed(() => visitorsStore.getVisitDates)
  const durationText = computed(() =>
    t('preappproval_duration_annotation.item_1', { days: policyDurationDays.value })
  )

  const visaData = computed(() => ({
    'preappproval_duration_annotation.item_3': getPolicyDates(),
    'preappproval_duration_annotation.item_2': visaNumber,
    'preappproval_duration_annotation.item_4': visaValidityDate
      ? formatDate(visaValidityDate)
      : '-',
  }))

  const { checkPrepareInboundAddon } = usePrepareInboundAddon()
  const onSubmit = () => {
    emit(
      'load',
      async () => {
        try {
          isLoading.value = true
          await visitorsStore.sendFormDataStepped({ travelType: policyDuration })

          gaService.sendClickProceedBySelectDuration(
            visitorsStore.getAnalyticsEntryType,
            durationText.value,
            policyDurationDays.value,
            coreStore.getInitialReferrer
          )

          if (checkPrepareInboundAddon(visitorsStore.holder, visitorsStore.entryValue)) {
            await getAbTest(INBOUND_ADDON_CODE)
            const isInboundAddonAbTest = INBOUND_ADDON_KEYS.includes(
              coreStore.getAbValue(INBOUND_ADDON_CODE)
            )

            if (isInboundAddonAbTest) {
              const res = await visitorsStore
                .inboundAddonCheck(
                  visitorsStore.holder.passport,
                  visitDates.value.startDate,
                  visitDates.value.tillDate
                )
                .catch((error) => {
                  const errorCode = error?.response?.data?.errorCode
                  if (errorCode !== inboundCheckResponse) {
                    emit('error', error)
                  }
                })
              visitorsStore.setInboundAddon(res?.success || false)
            } else {
              visitorsStore.setInboundAddon(false)
            }
          }

          emit('proceed')
        } finally {
          isLoading.value = false
        }
      },
      'travel-duration-submit',
      false
    )
  }

  onMounted(() => {
    emit(
      'load',
      async () => {
        await coreStore.getDurations()
        onPerformanceCheckEnd()
      },
      'travel-duration-mounted'
    )
  })
</script>

<template>
  <FlowCard
    :title="$t('changed_policy_duration_header')"
    :with-button="!isLoading"
    @click="onSubmit"
  >
    <AlertQC v-if="policyDurationDays" variant="warning" :subtitle="durationText" with-icon />

    <div class="pre-travel-duration">
      <ul class="pre-travel-duration__list">
        <li v-for="(value, key) in visaData" :key="key" class="pre-travel-duration__item">
          <span class="pre-travel-duration__key text-regular">{{ $t(key) }}</span>
          <span class="text-medium">{{ value }}</span>
        </li>
      </ul>
    </div>
    <template #button>
      <LoaderQC v-if="isLoading" variant="primary" size="extraLarge" class="pre-travel__loader" />
    </template>
  </FlowCard>
</template>

<style lang="scss" scoped>
  .pre-travel__loader {
    margin: 0 auto;
  }
  .pre-travel-duration {
    &__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: var(--colorTxtSrfPrimary);
      padding-block: var(--spacing-4);

      &:not(:last-child) {
        border-bottom: 1px solid var(--colorBrdrBtnOutlineDefault);
      }
    }

    &__key {
      color: var(--colorTxtSrfSecondary);
    }
  }
</style>
